import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import reactReplacePlaceholder from 'react-replace-placeholder';

import { iconsMap } from '../../InfoSnippetDecoupled/Snippet';
import { useMBDContext } from '../../common/MBD/useMBDContext';

import type { CheapestTimeToSnippetProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/CheapestTimeToProps';

import STYLES from './Snippet.module.scss';

type CheapestTimeToSnippetMbdProps = {
  mbdComponentName: string;
  index?: number;
};

type RelProps = { rel?: string };

const SnippetValue = ({
  linkUrl = undefined,
  relProps,
  // Multi-value lists suffix this per link so a getByTestId('linkurl') query
  // cannot match several elements at once.
  testId = 'linkurl',
  value = undefined,
}: {
  linkUrl?: string;
  relProps: RelProps;
  testId?: string;
  value?: CheapestTimeToSnippetProps['value'];
}) =>
  linkUrl ? (
    <BpkLink
      data-testid={testId}
      className={STYLES.Snippet__linkBody}
      href={linkUrl}
      {...relProps}
    >
      {value}
    </BpkLink>
  ) : (
    <BpkText textStyle={TEXT_STYLES.heading4}>{value}</BpkText>
  );

const Snippet = ({
  dofollow,
  icon,
  id,
  index = undefined,
  label,
  linkUrl,
  links,
  linksTemplate,
  mbdComponentName,
  value,
}: CheapestTimeToSnippetProps & CheapestTimeToSnippetMbdProps) => {
  const EmptyIcon = () => null;
  const Icon = iconsMap[icon] || EmptyIcon;

  const { buildMBDParam } = useMBDContext();

  const relProps = dofollow ? {} : { rel: 'nofollow' };

  // Multiple linked values are interpolated into the template on @@month_N@@.
  // The backend always sends a template matching links.length, so every
  // placeholder resolves and none is left behind in the markup.
  const renderBody = () => {
    if (links?.length && linksTemplate) {
      // reactReplacePlaceholder returns an array, so wrap it: .Snippet is a
      // flex column and each loose array item would otherwise become its own row.
      // No class needed — a flex item's display is blockified, so this bare span
      // is already a single block child with its values flowing inline inside it.
      return (
        <span>
          {reactReplacePlaceholder(
            linksTemplate,
            Object.fromEntries(
              links.map((link, linkIndex) => [
                `month_${linkIndex + 1}`,
                <SnippetValue
                  key={link.linkUrl ?? link.value}
                  {...link}
                  relProps={relProps}
                  testId={`linkurl-${linkIndex + 1}`}
                />,
              ]),
            ),
            '@@',
            '@@',
          )}
        </span>
      );
    }
    return <SnippetValue linkUrl={linkUrl} relProps={relProps} value={value} />;
  };

  return (
    <div
      className={STYLES.Snippet}
      data-testid="cheapestTimeToSnippet"
      // Use MBD component name provided by the parent component instead of from MBD context,
      // as this snippet has no context to know what specific kind of snippet it is
      data-tracking-element-id={
        index !== undefined ? `${mbdComponentName}_${index}` : mbdComponentName
      }
      data-tracking-common-params={buildMBDParam(mbdComponentName, {
        snippet_id: id ?? '',
      })}
    >
      <Icon className={STYLES.Snippet__icon} />
      <BpkText
        color={TEXT_COLORS.textPrimary}
        className={STYLES.Snippet__title}
        textStyle={TEXT_STYLES.bodyDefault}
      >
        {label}
      </BpkText>
      {renderBody()}
    </div>
  );
};

export default Snippet;
